
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStartAnalysis, useGetAnalysisStatus } from '../services/module1Service';
import { useBusinessConfig } from '../services/businessConfigService';
import { useToast } from '../context/ToastContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import LoadingIndicator from './LoadingIndicator';
import { Alert } from './ui/alert';
import Badge from './Badge';
import { FileText, Code2, Brain, Settings, ChevronDown, ChevronRight, RefreshCw, Clock } from 'lucide-react';
import blobService from '../services/storage/blobService';
import Module1Service from '../services/module1Service';

interface ModuleStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'reviewed';
  error?: string;
  lastUpdated?: string;
  debugInfo?: any;
}

const WorkflowDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { clientId, workflowId } = useParams<{ clientId: string, workflowId: string }>();
  const { showToast } = useToast();
  const { data: configData } = useBusinessConfig(workflowId || '');
  const [moduleStatuses, setModuleStatuses] = useState<Record<string, ModuleStatus>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDebugInfo, setExpandedDebugInfo] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch module statuses
  const fetchModuleStatuses = useCallback(async () => {
    if (!clientId || !workflowId) return;

    try {
      setIsLoading(true);
      setError(null);
      
      // Try to load workflow metadata and handle errors gracefully
      try {
        // First, try to get data from blob storage
        try {
          const metadata = await blobService.getLatestWorkflowMetadata(clientId, workflowId);
          if (metadata) {
            // Initialize statuses
            const statuses: Record<string, ModuleStatus> = {};

            // Check each module's results
            for (let i = 1; i <= 4; i++) {
              const moduleId = `module${i}`;
              try {
                const results = await blobService.getLatestModuleResults(clientId, workflowId, moduleId);
                
                if (results) {
                  statuses[moduleId] = {
                    status: results.status || 'completed',
                    error: results.error,
                    lastUpdated: new Date().toISOString(),
                    debugInfo: {
                      source: 'blob_storage',
                      raw: results,
                      timestamp: new Date().toISOString()
                    }
                  };
                } else {
                  statuses[moduleId] = { 
                    status: 'pending',
                    lastUpdated: new Date().toISOString(),
                    debugInfo: {
                      source: 'blob_storage',
                      message: 'No results found',
                      timestamp: new Date().toISOString()
                    }
                  };
                }
              } catch (error) {
                console.warn(`Failed to load results for ${moduleId}`, error);
                statuses[moduleId] = { 
                  status: 'pending',
                  debugInfo: {
                    source: 'blob_storage',
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: new Date().toISOString()
                  }
                };
              }
            }

            setModuleStatuses(statuses);
            return;
          }
        } catch (storageError) {
          console.error('Storage service error:', storageError);
          // Continue to fallback method
        }
        
        // Fallback: Get workflow info from the API
        try {
          const moduleService = Module1Service.getInstance();
          // For each module, set a pending status for now
          const statuses: Record<string, ModuleStatus> = {
            module1: { 
              status: 'pending',
              debugInfo: {
                source: 'api_fallback',
                message: 'Initialized with pending status',
                timestamp: new Date().toISOString()
              }
            },
            module2: { 
              status: 'pending',
              debugInfo: {
                source: 'api_fallback',
                message: 'Initialized with pending status',
                timestamp: new Date().toISOString()
              }
            },
            module3: { 
              status: 'pending',
              debugInfo: {
                source: 'api_fallback',
                message: 'Initialized with pending status',
                timestamp: new Date().toISOString()
              }
            },
            module4: { 
              status: 'pending',
              debugInfo: {
                source: 'api_fallback',
                message: 'Initialized with pending status',
                timestamp: new Date().toISOString()
              }
            }
          };
          
          // Try to get module1 status through the API
          try {
            const analysisStatus = await moduleService.getAnalysisStatus(workflowId, clientId);
            if (analysisStatus) {
              statuses.module1 = {
                status: analysisStatus.status === 'finalized' ? 'completed' : 
                        analysisStatus.status === 'failed' ? 'failed' : 'processing',
                error: analysisStatus.error,
                lastUpdated: new Date().toISOString(),
                debugInfo: {
                  source: 'api_response',
                  raw: analysisStatus,
                  timestamp: new Date().toISOString()
                }
              };
            }
          } catch (module1Error) {
            console.warn('Failed to get module1 status:', module1Error);
            statuses.module1.debugInfo = {
              source: 'api_error',
              error: module1Error instanceof Error ? module1Error.message : String(module1Error),
              timestamp: new Date().toISOString()
            };
          }
          
          setModuleStatuses(statuses);
        } catch (apiError) {
          console.error('API fallback error:', apiError);
          setError('Failed to load module statuses from both storage and API');
          setModuleStatuses({
            module1: { 
              status: 'pending',
              debugInfo: {
                source: 'fallback_error',
                error: apiError instanceof Error ? apiError.message : String(apiError),
                timestamp: new Date().toISOString()
              }
            },
            module2: { 
              status: 'pending',
              debugInfo: {
                source: 'fallback_error',
                error: apiError instanceof Error ? apiError.message : String(apiError),
                timestamp: new Date().toISOString()
              }
            },
            module3: { 
              status: 'pending',
              debugInfo: {
                source: 'fallback_error',
                error: apiError instanceof Error ? apiError.message : String(apiError),
                timestamp: new Date().toISOString()
              }
            },
            module4: { 
              status: 'pending',
              debugInfo: {
                source: 'fallback_error',
                error: apiError instanceof Error ? apiError.message : String(apiError),
                timestamp: new Date().toISOString()
              }
            }
          });
        }
      } catch (error) {
        console.error('Error fetching workflow metadata:', error);
        if (error instanceof Error && error.message === 'BlobServiceClient not initialized') {
          setError('Storage service not initialized. Using default values instead.');
        } else {
          setError('Failed to load workflow metadata');
        }
        
        // Set default pending status for all modules
        setModuleStatuses({
          module1: { 
            status: 'pending',
            debugInfo: {
              source: 'error_handler',
              error: error instanceof Error ? error.message : String(error),
              timestamp: new Date().toISOString()
            }
          },
          module2: { 
            status: 'pending',
            debugInfo: {
              source: 'error_handler',
              error: error instanceof Error ? error.message : String(error),
              timestamp: new Date().toISOString()
            }
          },
          module3: { 
            status: 'pending',
            debugInfo: {
              source: 'error_handler',
              error: error instanceof Error ? error.message : String(error),
              timestamp: new Date().toISOString()
            }
          },
          module4: { 
            status: 'pending', 
            debugInfo: {
              source: 'error_handler',
              error: error instanceof Error ? error.message : String(error),
              timestamp: new Date().toISOString()
            }
          }
        });
      }
    } catch (error) {
      console.error('Error fetching module statuses:', error);
      setError('Failed to load module statuses');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [clientId, workflowId]);

  // Initial load
  useEffect(() => {
    fetchModuleStatuses();
  }, [fetchModuleStatuses]);

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchModuleStatuses();
  };

  // Toggle debug info expansion
  const toggleDebugInfo = (moduleId: string) => {
    if (expandedDebugInfo === moduleId) {
      setExpandedDebugInfo(null);
    } else {
      setExpandedDebugInfo(moduleId);
    }
  };

  const getStatusBadge = (status: ModuleStatus['status']) => {
    let variant: 'default' | 'success' | 'warning' | 'danger' = 'default';
    let icon = null;
    
    switch(status) {
      case 'completed':
      case 'reviewed':
        variant = 'success';
        break;
      case 'processing':
        variant = 'warning';
        icon = <Clock className="w-3 h-3 mr-1 animate-pulse" />;
        break;
      case 'failed':
        variant = 'danger';
        break;
      default:
        variant = 'default';
    }
    
    return (
      <div className="flex items-center">
        {icon}
        <Badge text={status} variant={variant} />
      </div>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const renderDebugInfo = (moduleId: string, info?: any) => {
    if (!info) return null;
    
    return (
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium text-gray-500">Debug Information</h4>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              toggleDebugInfo(moduleId);
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            {expandedDebugInfo === moduleId ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </div>
        
        {expandedDebugInfo === moduleId && (
          <div className="bg-gray-50 p-3 rounded-md overflow-auto max-h-48 border border-gray-100">
            <div className="grid grid-cols-2 gap-1">
              <div className="text-gray-500">Source:</div>
              <div className="font-mono">{info.source}</div>
              
              <div className="text-gray-500">Timestamp:</div>
              <div className="font-mono">{formatDate(info.timestamp)}</div>
              
              {info.error && (
                <>
                  <div className="text-gray-500">Error:</div>
                  <div className="text-red-600 font-mono">{info.error}</div>
                </>
              )}
              
              {info.message && (
                <>
                  <div className="text-gray-500">Message:</div>
                  <div className="font-mono">{info.message}</div>
                </>
              )}
            </div>
            
            {info.raw && (
              <div className="mt-2">
                <div className="text-gray-500 mb-1">Raw data:</div>
                <pre className="text-xs bg-gray-100 p-2 rounded-md overflow-auto max-h-32 font-mono border border-gray-200">
                  {JSON.stringify(info.raw, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Convert URL parameters to numbers if they are numeric
  const numericClientId = clientId && /^\d+$/.test(clientId) ? parseInt(clientId, 10) : clientId;
  const numericWorkflowId = workflowId && /^\d+$/.test(workflowId) ? parseInt(workflowId, 10) : workflowId;
  
  // Always use the same IDs for navigation that were used to access this page
  // If they were numeric, we'll use numbers; if they were UUIDs, we'll use those
  const modules = [
    {
      id: 'module1',
      title: 'Keyword Analysis',
      description: 'Analyze text to identify key terms and patterns',
      icon: FileText,
      path: `/module1/client/${clientId}/${workflowId}`
    },
    {
      id: 'module2',
      title: 'Code Generation',
      description: 'Generate and review classification code',
      icon: Code2,
      path: `/module2/client/${clientId}/${workflowId}`
    },
    {
      id: 'module3',
      title: 'Semantic Framework',
      description: 'Define and manage semantic classification rules',
      icon: Brain,
      path: `/module3/client/${clientId}/${workflowId}`
    },
    {
      id: 'module4',
      title: 'Configuration',
      description: 'Configure and manage classification settings',
      icon: Settings,
      path: `/module4/client/${clientId}/${workflowId}`
    }
  ];

  if (!clientId || !workflowId) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <p>Missing client ID or workflow ID</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2 text-gray-800">Workflow Dashboard</h1>
          <p className="text-gray-600">Select a module to continue working with this workflow</p>
        </div>
        <Button 
          onClick={handleRefresh} 
          disabled={isRefreshing}
          variant="outline"
          className="flex items-center bg-white hover:bg-gray-50 border border-gray-200 shadow-sm px-4 py-2 rounded-lg transition-all"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin text-blue-500' : 'text-gray-500'}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh Status'}
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive" className="mb-6 rounded-lg">
          <p>{error}</p>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingIndicator />
      </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {modules.map((module) => (
            <Card 
              key={module.id}
              className="overflow-hidden border-0 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="relative">
                {/* Status indicator line at top of card */}
                <div className={`absolute top-0 left-0 right-0 h-1 ${
                  moduleStatuses[module.id]?.status === 'completed' || moduleStatuses[module.id]?.status === 'reviewed' 
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                    : moduleStatuses[module.id]?.status === 'processing' 
                    ? 'bg-gradient-to-r from-blue-400 to-indigo-500'
                    : moduleStatuses[module.id]?.status === 'failed'
                    ? 'bg-gradient-to-r from-red-400 to-rose-500'
                    : 'bg-gradient-to-r from-gray-300 to-gray-400'
                }`}></div>
                
                <div 
                  className="p-6 cursor-pointer bg-white"
                  onClick={() => {
                    console.log(`Navigating to: ${module.path}`);
                    navigate(module.path);
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-lg mr-4 ${
                        module.id === 'module1' ? 'bg-blue-50 text-blue-500' :
                        module.id === 'module2' ? 'bg-purple-50 text-purple-500' :
                        module.id === 'module3' ? 'bg-amber-50 text-amber-500' :
                        'bg-emerald-50 text-emerald-500'
                      }`}>
                        <module.icon className="w-5 h-5" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-800">{module.title}</h2>
                    </div>
                    {moduleStatuses[module.id] && (
                      <div>
                        {moduleStatuses[module.id].status === 'processing' ? (
                          <div className="px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-medium flex items-center">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-1.5 animate-pulse"></div>
                            Processing
                          </div>
                        ) : moduleStatuses[module.id].status === 'completed' || moduleStatuses[module.id].status === 'reviewed' ? (
                          <div className="px-3 py-1 rounded-full bg-green-50 border border-green-100 text-green-600 text-xs font-medium flex items-center">
                            <div className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></div>
                            Completed
                          </div>
                        ) : moduleStatuses[module.id].status === 'failed' ? (
                          <div className="px-3 py-1 rounded-full bg-red-50 border border-red-100 text-red-600 text-xs font-medium flex items-center">
                            <div className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></div>
                            Failed
                          </div>
                        ) : (
                          <div className="px-3 py-1 rounded-full bg-gray-50 border border-gray-100 text-gray-600 text-xs font-medium flex items-center">
                            <div className="w-2 h-2 rounded-full bg-gray-400 mr-1.5"></div>
                            Pending
                          </div>
                        )}
          </div>
                )}
              </div>
                  <p className="text-gray-600 mb-4">{module.description}</p>
                  
                  {moduleStatuses[module.id]?.lastUpdated && (
                    <p className="text-xs text-gray-500 flex items-center mb-3">
                      <Clock className="w-3 h-3 mr-1 text-gray-400" />
                      Last updated: {formatDate(moduleStatuses[module.id].lastUpdated)}
                    </p>
                  )}
                  
                  {moduleStatuses[module.id]?.error && (
                    <Alert variant="destructive" className="mt-2 mb-3 py-2 px-3 bg-red-50 border border-red-100 text-red-700 rounded-md">
                      <p className="text-sm">{moduleStatuses[module.id].error}</p>
                </Alert>
              )}
                  
                  {renderDebugInfo(module.id, moduleStatuses[module.id]?.debugInfo)}
                  
                  <div className="mt-4 flex justify-end">
                    <button 
                      className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        module.id === 'module1' ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' :
                        module.id === 'module2' ? 'bg-purple-50 text-purple-600 hover:bg-purple-100' :
                        module.id === 'module3' ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' :
                        'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(module.path);
                      }}
                    >
                      Open module
                    </button>
                  </div>
                </div>
            </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowDashboard; 